import type React from "react";
import type {authDetails, userDetails} from "redux/reducers/authSlice";
import {format, startOfYear} from "date-fns";
import {encrypt} from "./crypto";
import {
  API_TIMEOUTS_MILLISECONDS,
  AxiosErrorCode,
  CPSConstants,
  CPSMessages,
  CPSNumbers,
  FileExtension,
  FileMIMEType,
  HttpStatusCode,
  PageNames,
} from "./enumerations";
import {store} from "../redux/store";
import {getRequest, postRequest, setHeader} from "repository/genericRepository";
import {
  LOAD_ACTIVITY,
  SERVER_MESSAGE_ENPOINT,
  SERVER_MESSAGES_ENPOINT,
} from "./globalAPISettings";
import {logMessage} from "services/loggerService";
import {createTheme} from "@mui/material/styles";
import {Path, UseFormSetValue} from "react-hook-form";
import {
  HttpException,
  ServiceAuthenticationException,
} from "services/exceptionHandlerService";
import dayjs, {Dayjs} from "dayjs";
import {NavigateFunction} from "react-router-dom";
import {BreadCrumbsProps} from "./genericInterfaces";

interface ErrorProps {
  stack: string;
  request: {
    timeout: string;
  };
  config: {
    method: string;
    url: string;
    timeout: string;
  };
  code: string;
  message: string;
}

export const loggedMessage = (
  error: ErrorProps,
  errorMsg: string,
  pageName: string,
) => {
  return logMessage(
    "ERROR",
    `{ 
      "message" : "${error?.message}" , 
      "code" : "${error?.code}" , 
      "method" : "${error?.config?.method}" , 
      "url" : "${error?.config?.url}" , 
      "time-out" : "${error?.request?.timeout}" , 
      "error-details" : "${error?.stack}" ,
      "error-messages": "${errorMsg}"
    }`,
    pageName,
  );
};

export const removeAllWhitespace = (str: string | null | undefined): string => {
  return str!.replace(/\s/g, "");
};

export const firstFiveYears: string[] = Array.from({length: 5}, (_, index) =>
  format(startOfYear(new Date(new Date().getFullYear() + index, 0, 1)), "yyyy"),
);

export const truncateText = (text: string, maxlength: number) => {
  if (text?.length <= maxlength) {
    return text;
  }
  return text?.slice(0, maxlength) + "...";
};

export const scrollToTop = (
  top: number,
  behavior: ScrollBehavior | undefined,
) => {
  window.scrollTo({
    top: top,
    behavior: behavior,
  });
};

export const downloadExcelToBase64 = async (
  base64String: string,
  fileName: string,
) => {
  try {
    const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64String}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  } catch (error: any) {
    loggedMessage(error, "", PageNames.FACULTY);
  }
};

export const downloadExcelBase64 = (linkSource: string, fileName: string) => {
  try {
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  } catch (error: any) {
    loggedMessage(error, "", PageNames.FACULTY);
  }
};

export const fileExtensionManager = (
  extension: string,
  baseFile: string,
  fileName: string,
  setBaseString: React.Dispatch<React.SetStateAction<string>>,
  downloadExcelBase64: (base64String: string, fileName: string) => void,
) => {
  if (extension === FileExtension.PDF) {
    setBaseString(`data:${FileMIMEType.PDF};base64,${baseFile}`);
    const linkSource = `data:${FileMIMEType.PDF};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.DOCX) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.DOCX};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.DOC) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.DOC};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.XLSX) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.XLSX};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.XLS) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.XLS};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.PPTX) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.PPTX};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.PPT) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.PPT};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.RTF) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.RTF};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.MP3) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.MP3};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.MP4) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.MP4};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.XSL) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.XSL};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.ZIP) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.ZIP};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.PARTIAL) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.PARTIAL};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.WMV) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.WMV};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.MOV) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.MOV};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.MPEG) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.MPEG};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === FileExtension.TIFF) {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.TIFF};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  } else if (extension === "") {
    setBaseString("");
    const linkSource = `data:${FileMIMEType.OCTET};base64,${baseFile}`;
    downloadExcelBase64(linkSource, fileName);
  }
};

export const getFiletype = (extension: string) => {
  if (extension === FileExtension.PDF) {
    return `${FileMIMEType.PDF}`;
  } else if (extension === FileExtension.DOCX) {
    return `${FileMIMEType.DOCX}`;
  } else if (extension === FileExtension.DOC) {
    return `${FileMIMEType.DOC}`;
  } else if (extension === FileExtension.XLSX) {
    return `${FileMIMEType.XLSX}`;
  } else if (extension === FileExtension.PPTX) {
    return `${FileMIMEType.PPTX}`;
  } else if (extension === FileExtension.PNG) {
    return `${FileMIMEType.PNG}`;
  } else if (extension === FileExtension.JPG) {
    return `${FileMIMEType.JPG}`;
  } else if (extension === FileExtension.JPEG) {
    return `${FileMIMEType.JPEG}`;
  } else if (extension === FileExtension.BMP) {
    return `${FileMIMEType.BMP}`;
  } else if (extension === FileExtension.GIF) {
    return `${FileMIMEType.GIF}`;
  } else if (extension === FileExtension.WEBP) {
    return `${FileMIMEType.WEBP}`;
  } else {
    return `${FileMIMEType.HTML}`;
  }
};

export const isValidObject = function (obj: any) {
  return typeof obj !== "undefined" && obj !== null && obj !== "";
};

export const isArrayObjectEmpty = (arr: any[]) => {
  return (
    arr.length === 0 ||
    arr.every((obj: object) => Object.keys(obj).length === 0)
  );
};

export const convertStrToInt = (
  stringval: string,
  optional: string | number,
) => {
  const numericVal: RegExpMatchArray | null = stringval.match(/\d+/);
  return numericVal != null && numericVal[0] !== "1"
    ? Number(numericVal[0])
    : Number(optional);
};

export const formatToISOWithTrimedTime = (
  dateStr: string,
  timeStr: string,
): string | null => {
  try {
    const dateValue = dayjs(dateStr).format(
      CPSConstants.YEAR_MONTH_DATE_FORMAT_WITHOUT_TIME,
    );
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      throw new Error("Invalid date format. Expected YYYY-MM-DD.");
    }
    if (!timeStr?.trim()) {
      return `${dateValue}`;
    }

    let [timePart, msPart = "000"] = timeStr?.split(".");
    let [hours = "00", minutes = "00", seconds = "00"] = timePart?.split(":");

    const hh = hours.padStart(2, "0");
    const mm = minutes.padStart(2, "0");
    const ss = seconds.padStart(2, "0");
    const mmm = msPart.padEnd(3, "0");

    return `${dateValue}T${hh}:${mm}:${ss}.${mmm}`;
  } catch (error: any) {
    loggedMessage(error, "", PageNames.ACTIVITY);
    return null;
  }
};

export const formatToISO = (
  dateStr: string,
  timeStr: string,
): string | null => {
  try {
    const dateValue = dayjs(dateStr).format(
      CPSConstants.YEAR_MONTH_DATE_FORMAT_WITHOUT_TIME,
    );
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      throw new Error("Invalid date format. Expected YYYY-MM-DD.");
    }
    if (!timeStr?.trim()) {
      return `${dateValue}T00:00:00.000`;
    }

    let [timePart, msPart = "000"] = timeStr?.split(".");
    let [hours = "00", minutes = "00", seconds = "00"] = timePart?.split(":");

    const hh = hours.padStart(2, "0");
    const mm = minutes.padStart(2, "0");
    const ss = seconds.padStart(2, "0");
    const mmm = msPart.padEnd(3, "0");

    return `${dateValue}T${hh}:${mm}:${ss}.${mmm}`;
  } catch (error: any) {
    loggedMessage(error, "", PageNames.ACTIVITY);
    return null;
  }
};

export const removePDFBase64Header = (base64String: string) => {
  const regex = /^data:application\/(pdf|octet-stream);base64,/i;
  return base64String.replace(regex, "");
};

export const useAlertTimeout = (setShowAlert: (show: boolean) => void) => {
  setShowAlert(true);
};

export interface message {
  message: string;
  messageCode: number;
  isSuccessMessage: boolean;
  isValidationMessage: boolean;
  isErrorMessage: boolean;
}

export const initialMessage: message = {
  message: "",
  messageCode: 0,
  isSuccessMessage: false,
  isValidationMessage: false,
  isErrorMessage: false,
};
export interface messages {
  message: string;
  messageCode: number;
  isSuccessMessage: boolean;
  isValidationMessage: boolean;
  isErrorMessage: boolean;
  messages: any[];
}

export const initialMessages: messages[] = [
  {
    message: "",
    messageCode: 0,
    isSuccessMessage: false,
    isValidationMessage: false,
    isErrorMessage: false,
    messages: [],
  },
];

export const getServerMessage = async (messageCode: number) => {
  try {
    const authState = store.getState();
    const APIHeaders = {
      Authorization: `Bearer ${isValidObject(authState.auth?.authDetails?.token) ? authState.auth?.authDetails?.token : process.env.REACT_APP_API_AUTH_TOKEN}`,
    };
    const responseData = await getRequest({
      url: `${SERVER_MESSAGE_ENPOINT}?messageCode=${messageCode}`,
      headers: setHeader(APIHeaders),
      timeout: API_TIMEOUTS_MILLISECONDS.SERVER_MESSAGE_ENPOINT,
      params: {},
    });
    if (isValidObject(responseData)) {
      return responseData;
    }
  } catch (error: any) {
    loggedMessage(error, "", PageNames.MESSAGES);
  }
};

export const getServerMessages = async (messageCode: string) => {
  try {
    const authState = store.getState();

    const APIHeaders = {
      Authorization: `Bearer ${isValidObject(authState.auth?.authDetails?.token) ? authState.auth?.authDetails?.token : process.env.REACT_APP_API_AUTH_TOKEN}`,
    };
    const responseData = await getRequest({
      url: `${SERVER_MESSAGES_ENPOINT}?messageCodes=${messageCode}`,
      headers: setHeader(APIHeaders),
      timeout: API_TIMEOUTS_MILLISECONDS.SERVER_MESSAGES_ENPOINT,
      params: {},
    });
    if (isValidObject(responseData)) {
      return responseData?.messages;
    }
  } catch (error: any) {
    loggedMessage(error, "", PageNames.MESSAGES);
  }
};

export interface addressDetails {
  addressType: string;
  address: string;
}
export interface facultyDetailsRecords {
  messageCode: number;
  message: string;
  isSuccessMessage: boolean;
  isErrorMessage: boolean;
  isValidationMessage: boolean;
  salutation: string;
  firstName?: string;
  middleInitial: string;
  lastName?: string;
  suffix: string;
  speciality1: string;
  speciality2: string;
  credentials: string;
  boardCertification1: string;
  boardCertification2: string;
  brandDisease: string;
  affiliation: string;
  facultyAddresses: addressDetails[];
  telephoneNumber: string;
  telephoneNumberExtension: string;
  mobileNumber: string;
  faxNumber: string;
  comment: string;
  email: string;
  vmail: string;
  assistantName: string;
  assistantPhone: string;
  assistantPagerNumber: string;
  assistantEmail: string;
  approverName1: string;
  approverEmail1: string;
  approverName2: string;
  approverEmail2: string;
}
export const defaultFacultyDetails: facultyDetailsRecords = {
  messageCode: 0,
  message: "",
  isSuccessMessage: false,
  isErrorMessage: false,
  isValidationMessage: false,
  salutation: "",
  firstName: "",
  middleInitial: "",
  lastName: "",
  suffix: "",
  speciality1: "",
  speciality2: "",
  credentials: "",
  boardCertification1: "",
  boardCertification2: "",
  brandDisease: "",
  affiliation: "",
  facultyAddresses: [
    {
      addressType: "",
      address: "",
    },
  ],
  telephoneNumber: "",
  telephoneNumberExtension: "",
  mobileNumber: "",
  faxNumber: "",
  comment: "",
  email: "",
  vmail: "",
  assistantName: "",
  assistantPhone: "",
  assistantPagerNumber: "",
  assistantEmail: "",
  approverName1: "",
  approverEmail1: "",
  approverName2: "",
  approverEmail2: "",
};

export const base64ToBlob = (base64: any, fileExtension: string = ".pdf") => {
  const type = getFiletype(fileExtension);
  const binStr = atob(base64);
  const len = binStr.length;
  const arr = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    arr[i] = binStr.charCodeAt(i);
  }
  return new Blob([arr], {type: type});
};

export const downloadBase64 = (base64: string, fileName: string) => {
  const linkSource = `${base64}`;
  const downloadLink = document.createElement("a");
  downloadLink.href = linkSource;
  downloadLink.download = fileName;
  downloadLink.click();
};

export const formatInput = (value: string) => {
  const formattedValue = value.replace(/[^0-9]/g, "");
  return formattedValue;
};
export const IsValidSuffix = (s: string) => {
  const re = /^(?!.*?([,.\s])\1)[\w,.\s\x20]+$/;
  const reg = /^([0-9a-zA-Z])/;
  if (re.test(s)) return reg.test(s);
  else return re.test(s);
};

export const isNumericWithMultiple = (id: any) => {
  return /^(\d+\s?)*\d+$/.test(id);
};

export const IsNumeric = (obj: any) => {
  const regex = /^[0-9]+$/;
  let passedVal = obj;
  passedVal = passedVal.trim();
  let IsNumber = false;
  if (regex.test(passedVal)) {
    IsNumber = true;
  }
  return IsNumber;
};

export const CheckValidFormat = (obj: {value: string}) => {
  if (obj.value.length > 0) {
    const matchArray = /[^a-zA-Z0-9]/;
    return matchArray.test(obj.value);
  }
};
export const CheckValidFormatForEnterPriseId = (obj: {value: string}) => {
  if (obj.value.length > 0) {
    const matchArray = /^ENTID_[a-zA-Z0-9]+$/i;
    return !matchArray.test(obj.value);
  }
};

export const CheckValidCostPercent = (value: string) => {
  if (value.length > 0) {
    const matchArray = /^[0-9]*$/;
    return matchArray.test(value);
  }else {
    return false
  }
};

export const AppendZero = (number: any, length: any) => {
  let zeroAppend = number;
  while (zeroAppend.length < length) {
    zeroAppend = "0" + zeroAppend;
  }

  return zeroAppend;
};

export const isWhitespace = (s: string) => {
  let i;
  const whitespace = " \t\n\r";
  // Is s empty?
  if (s?.length === 0) return true;

  // Search through string's characters one by one
  // until we find a non-whitespace character.
  // When we do, return false; if we don't, return true.

  for (i = 0; i < s.length; i++) {
    // Check that current character isn't whitespace.
    const c = s.charAt(i);

    if (!whitespace.includes(c)) return false;
  }

  // All characters are whitespace.
  return true;
};
export const hasValue = (v: string | null | undefined) =>
  v !== null && v !== undefined && v.trim() !== "";
export const isAlphanumeric = (str: string) => {
  return /^[a-zA-Z0-9/".*"/()-]+$/.test(str);
};

export const acceptOnlyAlphanumeric = (value: string): boolean => {
  const alphanumericRegex = /^[a-zA-Z0-9\s]*$/;
  return alphanumericRegex?.test(value);
};

export const isValidSearchName = (strToCheck: string) => {
  const objNamePattern =
    /^[A-Za-z\u00BF-\u0259]+[A-Za-z\u00BF-\u0259\u2018\u2019\d\s\.\,\*\'\#\(\)\"\-\/\&]*$/;
  return objNamePattern.test(strToCheck);
};

export const isValidName = (strToCheck: string) => {
  const objNamePattern =
    /[A-Za-z\u00BF-\u0259]+[A-Za-z\u00BF-\u0259\u2018\u2019\d\s\w$*^.()""-/&]/;
  return objNamePattern.test(strToCheck);
};

export const isValidTLMapText = (strToCheck: string) => {
  const objPattern = /^(CA-|DFCA-)[0-9]+$/;
  return objPattern.test(strToCheck);
};

export const isValidFullSearchName = (strToCheck: string) => {
  const objNamePattern = /^[a-zA-Z0-9_'-]+$/;
  return objNamePattern.test(strToCheck);
};

export const isValidMiddleName = (strToCheck: string) => {
  const objNamePattern = /^[a-zA-Z0-9_'""-]+$/;
  return objNamePattern.test(strToCheck);
};
export const isValidLicenseNumber = (strToCheck: string) => {
  const objNamePattern = /^[a-zA-Z\s0-9-]+$/;
  return objNamePattern.test(strToCheck);
};
export const isValidclientId = (strToCheck: string) => {
  const objNamePattern = /^[a-zA-Z0-9]+$/;
  return objNamePattern.test(strToCheck);
};
export const isValidFormatForEnterPriseId = (strToCheck: string) => {
  const matchArray = /^ENTID_[a-zA-Z0-9]+$/i;
    return matchArray.test(strToCheck);
};


export const isValidCity = (strToCheck: string) => {
  const objPattern = /^[ A-Za-z _$@./#&+-/*/%/^{}():""']*$/;
  return objPattern.test(strToCheck);
};

//Validates that there is only one character and its a Alphabet
export const isOnlyAlphabet = (strToCheck: string) => {
  const objNamePattern = /^[A-Za-z]$/;
  return objNamePattern.test(strToCheck);
};

export const isValidWebsiteAddress = (WebsiteName: string) => {
  const RegexAssistName =
    /^(([\w]+:)?\/\/)?(([\d\w]|%[a-fA-f\d]{2,2})+(:([\d\w]|%[a-fA-f\d]{2,2})+)?@)?([\d\w][-\d\w]{0,253}[\d\w]\.)+[\w]{2,4}(:[\d]+)?(\/([-+_~.\d\w]|%[a-fA-f\d]{2,2})*)*(\?(&?([-+_~.\d\w]|%[a-fA-f\d]{2,2})=?)*)?(#([-+_~.\d\w]|%[a-fA-f\d]{2,2})*)?$/;
  return RegexAssistName.test(WebsiteName);
};
export const fileToBase64 = (file: any, callbackfun: any) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => {
    callbackfun(null, reader.result);
  };
  reader.onerror = error => {
    callbackfun(error, null);
  };
};

export const CapitaliseFirstLetter = (sentence: string) => {
  return sentence.replace(/(^\w{1})/g, letter => letter.toUpperCase());
};
export const generateRandomID = () => {
  return Math.random().toString(16).slice(2);
};
export const validPostalCode = (code: string) => {
  const RegexPostalCode = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
  return RegexPostalCode.test(code);
};
export const isValidMobileNumber = (mobileNumber: string) => {
  const regex =
    /^([+]?\d{10}|[+]?\d{3}\.\d{3}\.\d{4}|[+]?\d{3}-\d{3}-\d{4}|[+]?\d{3}\s\d{3}\s\d{4}|[+]?\(\d{3}\)\s\d{3}-\d{4}|[+]?\(\d{3}\)\d{3}-\d{4}|[+]?\(\d{3}\)\(\d{3}\)\(\d{4}\)|[+]?\(\d{3}\)\(\d{3}\)\d{4}|[+]?\(\d{3}\)\d{7}|[+]?\(\d{3}\)\d{3}\s\d{4}|[+]?\(\d{3}\)\s\d{3}\s\d{4})$/;
  return regex.test(mobileNumber);
};
export const isValidTelephoneNumber = (telNumber: string) => {
  const RegexTel =
    /^([+]?\d{10}|[+]?\d{3}\.\d{3}\.\d{4}|[+]?\d{3}-\d{3}-\d{4}|[+]?\d{3}\s\d{3}\s\d{4}|[+]?\(\d{3}\)\s\d{3}-\d{4}|[+]?\(\d{3}\)\d{3}-\d{4}|[+]?\(\d{3}\)\(\d{3}\)\(\d{4}\)|[+]?\(\d{3}\)\(\d{3}\)\d{4}|[+]?\(\d{3}\)\d{7}|[+]?\(\d{3}\)\d{3}\s\d{4}|[+]?\(\d{3}\)\s\d{3}\s\d{4})$/;
  return RegexTel.test(telNumber);
};
export const isValidLillyPersonID = (LillyPersonID: string) => {
  const RegexPersonID = /^([a-zA-Z0-9_\s-]+)$/;
  return RegexPersonID.test(LillyPersonID);
};
export const isValidPagerNumber = (pagerNumber: string) => {
  const RegexPager =
    /^([+]?\d{10}|[+]?\d{3}\.\d{3}\.\d{4}|[+]?\d{3}-\d{3}-\d{4}|[+]?\d{3}\s\d{3}\s\d{4}|[+]?\(\d{3}\)\s\d{3}-\d{4}|[+]?\(\d{3}\)\d{3}-\d{4}|[+]?\(\d{3}\)\(\d{3}\)\(\d{4}\)|[+]?\(\d{3}\)\(\d{3}\)\d{4}|[+]?\(\d{3}\)\d{7}|[+]?\(\d{3}\)\d{3}\s\d{4}|[+]?\(\d{3}\)\s\d{3}\s\d{4})$/;
  return RegexPager.test(pagerNumber);
};
export const isValidFaxNumber = (faxNumber: string) => {
  const RegexFax =
    /^([+]?\d{10}|[+]?\d{3}\.\d{3}\.\d{4}|[+]?\d{3}-\d{3}-\d{4}|[+]?\d{3}\s\d{3}\s\d{4}|[+]?\(\d{3}\)\s\d{3}-\d{4}|[+]?\(\d{3}\)\d{3}-\d{4}|[+]?\(\d{3}\)\(\d{3}\)\(\d{4}\)|[+]?\(\d{3}\)\(\d{3}\)\d{4}|[+]?\(\d{3}\)\d{7}|[+]?\(\d{3}\)\d{3}\s\d{4}|[+]?\(\d{3}\)\s\d{3}\s\d{4})$/;
  return RegexFax.test(faxNumber);
};
export const isValidEmailId = (email: any) => {
  const RegexEmail =
    /^([\w'+-]+(?:\.[\w'+-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2,8})?)$/i;
  return RegexEmail.test(email);
};

export const isValidEmailAddresses = (email: string): boolean => {
  const RegexEmail =
    /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2,8})?)$/i;
  const emailList = email.split(";").map((e: string) => e.trim());
  return emailList.every((e: string) => RegexEmail.test(e));
};

export const isValidAssistantName = (assistantName: string) => {
  const RegexAssistName =
    /^[A-Za-z\u00BF-\u0259]+[A-Za-z\u00BF-\u0259\u2018\u2019\d\s.,*'#()""""\-/&]*$/;
  return RegexAssistName.test(assistantName);
};
export const isValidVMail = (faxNumber: string) => {
  const RegexVmail =
    /^([+]?\d{10}|[+]?\d{3}\.\d{3}\.\d{4}|[+]?\d{3}-\d{3}-\d{4}|[+]?\d{3}\s\d{3}\s\d{4}|[+]?\(\d{3}\)\s\d{3}-\d{4}|[+]?\(\d{3}\)\d{3}-\d{4}|[+]?\(\d{3}\)\(\d{3}\)\(\d{4}\)|[+]?\(\d{3}\)\(\d{3}\)\d{4}|[+]?\(\d{3}\)\d{7}|[+]?\(\d{3}\)\d{3}\s\d{4}|[+]?\(\d{3}\)\s\d{3}\s\d{4})$/;
  return RegexVmail.test(faxNumber);
};
export const extractQueryParams = (queryString: string): any => {
  const regex = /([^&=]+)=([^&]*)/g;
  let match: RegExpExecArray | null;
  const params: any = {};
  while ((match = regex.exec(queryString)) !== null) {
    const key = decodeURIComponent(match[1]);
    const value = decodeURIComponent(match[2]);
    params[key] = value;
  }
  return params;
};

export const isValidDate = (d: any) => {
  return d instanceof Date && !isNaN(d.valueOf());
};

export const customIconUrl = (data: string | undefined) => {
  const tempCount = data?.split(":")?.[1]?.trim();
  const count = isValidObject(Number(tempCount))
    ? Number(tempCount)
    : CPSNumbers.ZERO;
  return (
    "data:image/svg+xml;utf-8," +
    encodeURIComponent(`
  <svg width="50" height="50" xmlns="http://www.w3.org/2000/svg">
    <circle cx="25" cy="25" r="20" fill="${Number(count) <= CPSNumbers.FIVE ? "#4A90E2" : Number(count) >= CPSNumbers.SIX && Number(count) <= CPSNumbers.TEN ? "#E94B6B" : "#F5A623"}" />
    <text x="50%" y="50%" alignment-baseline="middle" text-anchor="middle" fill="white" font-size="12">${data}</text>
  </svg>
`)
  );
};

export const isValidURL = (url: string) => {
  const urlPattern =
    /^(https):\/\/(([a-z\d]([a-z\d-]*[a-z\d])?\.)+[a-z]{2,}|localhost)(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(\#[-a-z\d_]*)?$/i;
  return urlPattern.test(url);
};

export const compareObjectsExcluding = (
  obj1: any,
  obj2: any,
  excludedKeys: string,
) => {
  const keys1 = Object.keys(obj1).filter(key => !excludedKeys.includes(key));
  const keys2 = Object.keys(obj2).filter(key => !excludedKeys.includes(key));
  // Ensure both objects have the same keys excluding the excluded ones
  if (keys1.length !== keys2.length) {
    return;
  }

  // Compare values of non-excluded properties
  for (const key of keys1) {
    if (`${obj1[key]}` !== `${obj2[key]}`) {
      return true;
    }
  }
};

export const getPrevBreadcrumbForContactInfo = (subMenuId: string | null) => {
  const userPref = store.getState();
  if (isValidObject(subMenuId)) {
    const getBreadCrumbs = userPref?.auth?.userPreferences?.breadcrumbs.filter(
      item => {
        return item.menuItemId === Number(subMenuId);
      },
    );
    getBreadCrumbs.sort((a, b) => a.displayOrder - b.displayOrder);
    const index = getBreadCrumbs.findIndex(item => item.id === 47);
    return getBreadCrumbs[index - 1]?.pageToLoad;
  } else {
    return subMenuId;
  }
};
export const isValidAmount = (amount: string) => {
  const RegexAmount = /(?:^|\s)(\d{1,7}(?:,\d{3})*(?:\.\d+)?)(?=\s|$)/g;
  return RegexAmount.test(amount);
};

export const dateToFormatedString = (
  date?: Date,
  dateformat: string = "MM/dd/YYYY hh:mm a",
) => {
  return date ? format(new Date(date), dateformat) : "";
};
export const dateToString = (
  date?: Date,
  dateformat: string = "MM/dd/yyyy",
) => {
  return date ? format(new Date(date), dateformat) : "";
};
export const maskingPhone = (value: string) => {
  // Format the input value as (123) 456-7890
  if (!value) {
    return "";
  }
  const formattedValue = value
    ?.replace(/\D/g, "")
    ?.replace(/(\d{3})(\d{1,3})?(\d{1,4})?/, "($1) $2-$3")
    ?.replace(/\D+$/, "");
  return formattedValue;
};

export const removeMasking = (str: string) => {
  if (str) {
    return str?.replace(/[^A-Z0-9]/gi, "");
  }
  return "";
};

export const addMasking = (str: string) => {
  if (str) {
    return str
      ?.replace(/\D+/g, "")
      .replace(/^(\d{3})(\d{3})(\d{4}).*/, "$1-$2-$3");
  }
  return "";
};

export const formatInputwithcomma = (value: string) => {
  const formattedValue = value.replace(/[^0-9,.]/g, "");
  return formattedValue;
};
export const formatAmount = (value: any) => {
  if (value === null) {
    return Number("00.00");
  } else return Number(value);
};

export const formatUSAmount = (value: any) => {
  if (!isValidObject(value)) {
    return "";
  }

  return isNaN(Number(value))
    ? value
    : value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};
export const acceptOnlyNumeric = (
  event: React.ChangeEvent<HTMLInputElement>,
) => {
  const {value} = event.target;
  if (/^\d*$/.test(value)) {
    return value;
  } else if (value.length > 0) {
    return value.substring(0, value.length - 1);
  }
};

export const acceptNumericGreaterThanZero = (
  event: React.ChangeEvent<HTMLInputElement>,
) => {
  const value = acceptOnlyNumeric(event);
  if (Number(value) > 0) {
    return value;
  }
  return "";
};

export const theme = createTheme({
  typography: {
    fontFamily: CPSConstants.FONT_FAMILY as string,
  },
});

export const isValidJSON = (jsonString: string) => {
  try {
    JSON.parse(jsonString);
    return true;
  } catch {
    return false;
  }
};

export const USDollar = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const formatToTwoDecimals = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const isValidRuleName = (ruleName: string) => {
  const RegexRuleMail = /^[a-zA-Z0-9 `~!@#$%^&*()_+-={}|\:;’<>?,./[]+$/;
  return RegexRuleMail.test(ruleName);
};

export const getFileExtension = (str: string) => {
  const lastIndex = str?.lastIndexOf(".");
  if (lastIndex !== -1) {
    return str?.substring(lastIndex)?.toLowerCase();
  } else {
    return "";
  }
};

export const removeFileExtensions = (fileName: string): string => {
  return fileName?.replace(/(\.\w+)+.*$/, "");
};

export const compareIds = (arrayOfObjects: any[], idsToCompare: any[]) => {
  if (!Array.isArray(arrayOfObjects) || !Array.isArray(idsToCompare)) {
    return "";
  }

  if (idsToCompare.length === 0) {
    return true;
  }

  return idsToCompare.some(id =>
    arrayOfObjects.some(obj => obj && obj.id === id),
  );
};

export const isCharacterPresent = (data: string) => {
  const regexIsChar = /[a-zA-Z]/;

  return regexIsChar.test(data);
};

export const isValidGroupName = (data: string) => {
  const regex =
    /^[A-Za-z0-9\u00BF-\u0259]+[A-Za-z0-9\u00BF-\u0259\u2018\u2019\d\s\.\*\'\,\#\(\)\""\-\/\&\_\!\%\$\@\:\;]*$/;
  return regex.test(data);
};

export const isSpecialCharacter = (str: string) => {
  return /[!@#$%^&*()\-+={}[\]:;"'<>,.?\/|\\]/.test(str);
};
export const toLowerCaseFirstLetter = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toLowerCase() + str.slice(1);
};

export const isValidTemplateName = (name: string) => {
  const RegexAmount = /^[a-zA-Z0-9 ()&!_#-:]+$/;
  return RegexAmount.test(name);
};

export const isValidFolder = (str: string) => {
  return /[\\<>:?*|"#%]/g.test(str);
};

export const displayEllipsis = (content: string, contentLength: number) => {
  return content?.length > contentLength
    ? content?.substring(0, contentLength) + "..."
    : content;
};

export const isValidSubjectLine = (str: string) => {
  return /^[a-zA-Z0-9\-_'!.,?&+=/()\[\]#\s:;™®]+$/.test(str);
};

export const ValidateSpecialCharacters = (e: string) => {
  var regex = /^[a-zA-Z0-9 `~!@#$%^&*()_+-={}|\:;’<>?,./[]+$/;
  e = e
    .replace(/""/g, "Esc")
    .replace(/]/g, "Esc")
    .replace(/\\/g, "Esc")
    .replace(/'/g, "Esc")
    .replace(/’/g, "Esc")
    .replace(/”/g, "Esc");
  return regex.test(e) ? true : false;
};

export const decode = (str: string) => {
  return str?.replace(/!#(\d+);/g, function (match, dec) {
    return String.fromCharCode(dec);
  });
};

export const encode = (str: string): string => {
  const buf: string[] = [];
  for (let i = str.length - 1; i >= 0; i--) {
    buf.unshift(["!#", str.charCodeAt(i), ";"].join(""));
  }
  return buf.join("");
};

export const removeZero = (input: string): string => {
  const parts: string[] = input?.split(",");
  const filtered: string[] = parts?.filter(
    num => num?.trim() !== String(CPSNumbers.ZERO),
  );
  return filtered?.join(",");
};

export const handlePositiveNumberChange = <T extends Record<string, any>>(
  e: React.ChangeEvent<any>,
  fieldName: Path<T>,
  setValue: UseFormSetValue<T>,
) => {
  const input = e.target.value;

  if (/^\d*$/.test(input)) {
    if (input === "" || parseInt(input, 10) > 0) {
      setValue(fieldName, input as any);
    }
  }
};

export const printContent = (printRef: React.RefObject<HTMLDivElement>) => {
  if (printRef.current) {
    const printContents = printRef.current.innerHTML;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  }
};

export const validateNumDollarFloat = (input: string | any): boolean => {
  const regex = /^\$?[1-9]\d*?(?:,?\d{3})*(?:\.\d{2})?$/g;
  return regex.test(input);
};

export const convertToBoolean = (value: string | null) => {
  if (value === "true") {
    return true;
  } else if (value === "false") {
    return false;
  } else if (value === null) {
    return null;
  }
};

export const getNextId = (arr: {id: number}[]) => {
  if (arr?.length === 0) return 1;
  return Math?.max(...arr?.map(item => item?.id)) + 1;
};
export const isValidDecimal = (value: string) => {
  if (value === "") return true;
  return /^[0-9]*\.?[0-9]*$/.test(value);
};

export const parseQueryString = (query: string): Record<string, any> => {
  const result: Record<string, any> = {};
  const params = new URLSearchParams(query);

  for (const [key, value] of params.entries()) {
    if (value === "true") {
      result[key] = true;
    } else if (value === "false") {
      result[key] = false;
    } else if (!isNaN(Number(value)) && value.trim() !== "") {
      result[key] = Number(value);
    } else {
      result[key] = value;
    }
  }

  return result;
};

export const formatWithoutRounding = (value: number | string): string => {
  const str = String(value);
  if (str.includes(".")) {
    const [intPart, decPart = ""] = str?.split(".");
    const padded = decPart?.padEnd(2, "0");
    return `${intPart}.${padded}`;
  } else {
    return `${str}.00`;
  }
};

export const formatNumber = (
  val: number | string | null,
): number | string | null => {
  const parts = val?.toString()?.split(".");
  const tempNum = Number(val);
  if (!isValidObject(val)) {
    return null;
  }
  if (parts?.length === 1) {
    return tempNum.toFixed(2);
  }
  const decimalLength = parts?.[1]?.length;

  if (Number(decimalLength) <= 2) {
    return tempNum.toFixed(2);
  }
  return tempNum.toFixed(3);
};

export const formatCurrency = (
  value: number | string,
  isDollarRequired = true,
): string => {
  const num = Number(value);

  if (isNaN(num)) return `${isDollarRequired ? "$" : ""}0.00`;

  const [intPart, decPartRaw = ""] = String(value)?.split(".");
  const decPart = decPartRaw?.padEnd(2, "0")?.slice(0, 2);

  const formattedInt = parseInt(intPart, 10)?.toLocaleString("en-US");

  return `${isDollarRequired ? "$" : ""}${formattedInt}.${decPart}`;
};

export const strCompareTrimedLower = (a: string, b: string) => {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
};

export const handleAxiosErrors = (
  error: any,
  setShowAlert?: React.Dispatch<React.SetStateAction<boolean>>,
  setServerValidation?: React.Dispatch<React.SetStateAction<message>>,
  isScrollToTop?: boolean,
) => {
  const data = {pagename: PageNames.ADMIN};
  if (
    error?.code === AxiosErrorCode.ERR_BAD_REQUEST ||
    error?.code === AxiosErrorCode.ERR_BAD_RESPONSE
  ) {
    if (
      error?.response?.status === HttpStatusCode.UNAUTHORIZED ||
      error?.response?.status === HttpStatusCode.FORBIDDEN
    ) {
      ServiceAuthenticationException(error, data, error?.response?.status);
    } else if (error?.response?.status === HttpStatusCode.BAD_REQUEST) {
      if (isScrollToTop) {
        scrollToTop(0, "smooth");
      }
      setShowAlert && setShowAlert(true);
      setServerValidation && setServerValidation(error?.response?.data);
    } else if (error?.response?.status === HttpStatusCode.NOT_FOUND) {
      if (isScrollToTop) {
        scrollToTop(0, "smooth");
      }
      setShowAlert && setShowAlert(true);
      setServerValidation &&
        setServerValidation({
          ...initialMessage,
          message: CPSMessages.SERVICE_NOT_FOUND,
          isErrorMessage: true,
        });
    } else if (
      error?.response?.status === HttpStatusCode.INTERNAL_SERVER_ERROR
    ) {
      HttpException(error, data);
      if (isScrollToTop) {
        scrollToTop(0, "smooth");
      }
      setShowAlert && setShowAlert(true);
      setServerValidation &&
        setServerValidation({
          ...initialMessage,
          message: CPSMessages.INTERNAL_SERVER_ERROR,
          isErrorMessage: true,
        });
    } else {
      if (isScrollToTop) {
        scrollToTop(0, "smooth");
      }
      setShowAlert && setShowAlert(true);
      setServerValidation &&
        setServerValidation({
          ...initialMessage,
          message: error?.message,
          isErrorMessage: true,
        });
    }
  } else if (error?.code === AxiosErrorCode.ECONNABORTED) {
    if (isScrollToTop) {
      scrollToTop(0, "smooth");
    }
    setShowAlert && setShowAlert(true);
    setServerValidation &&
      setServerValidation({
        ...initialMessage,
        message: CPSMessages.TIMEOUT_ERROR,
        isErrorMessage: true,
      });
  } else if (error?.code === AxiosErrorCode.ERR_NETWORK) {
    if (isScrollToTop) {
      scrollToTop(0, "smooth");
    }
    setShowAlert && setShowAlert(true);
    setServerValidation &&
      setServerValidation({
        ...initialMessage,
        message: CPSMessages.NETWORK_ERROR,
        isErrorMessage: true,
      });
  } else {
    if (isScrollToTop) {
      scrollToTop(0, "smooth");
    }
    setShowAlert && setShowAlert(true);
    setServerValidation &&
      setServerValidation({
        ...initialMessage,
        message: error?.message,
        isErrorMessage: true,
      });
  }
};

export const acceptOnlyPositiveNumber = (newValue: string): boolean => {
  // Allow only digits (no negatives, no decimals, no leading 0)
  return /^[1-9]\d*$/?.test(newValue) || newValue === "";
};

export const validateActivityIdentity = (value: string): boolean => {
  const alphabetic = /^[a-zA-Z]+$/;
  if (!isNaN(Number(value))) {
    return true;
  }

  const shortName = value?.substring(0, 3);
  const numberPart = value?.substring(3);

  const isShortNameValid =
    shortName?.length === 3 && alphabetic?.test(shortName);
  const isNumberPartValid =
    numberPart?.length > 0 && !isNaN(Number(numberPart));

  return isShortNameValid && isNumberPartValid;
};

export const isValidAlphaNumericBasicRegex = (value: string): boolean => {
  return /^[A-Za-z0-9 .,\-\r\n]*$/.test(value);
};

export const isValidZipCode = (code: string) => {
  const RegexPostalCode = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
  return RegexPostalCode.test(code);
};

export const normalize = (value: string | Date | Dayjs): string | Date => {
  if (typeof value === "string") return value;
  if (value instanceof Date) return value;
  return value.toDate(); // convert dayjs -> JS Date
};
export const setTimeToDateString = (
  date: string | Date | Dayjs,
  time: string | Date | Dayjs,
  timeType: string,
) => {
  let updatedDate = new Date(normalize(date));

  let timeString = isValidObject(time)
    ? format(normalize(time), "MM/dd/yyyy hh:mm aa")
    : "";

  let timeArr = timeString?.split(" ")[1];
  let timeFormat: string[] = timeArr?.split(":");

  if (timeFormat?.length) {
    updatedDate.setHours(Number(timeFormat[0]) + (timeType === "PM" ? 12 : 0));
    updatedDate.setMinutes(Number(timeFormat[1]));
    updatedDate.setSeconds(0);
    updatedDate.setMilliseconds(0);
  }

  return updatedDate;
};

export const isDefaultDateString = (dateStr: string): boolean => {
  return dateStr.startsWith("0001-01-01");
};

export const convertUtcToEasternTime = (utcDateString: Date) => {
  // Create a Date object from the UTC date string
  // Ensure the input is treated as UTC by using 'Z' or parsing it with Date.UTC()
  const utcDate = new Date(utcDateString);

  // Create an Intl.DateTimeFormat object for the Eastern Time Zone (America/New_York)
  // Specify the desired formatting options
  const easternTimeFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true, // Use 24-hour format
  });

  // Format the UTC date into Eastern Time
  const easternTimeString = easternTimeFormatter.format(utcDate);

  return easternTimeString;
};

export const fetchDateFieldValidation = (
  fieldName: string,
  messageData: message,
): message => {
  const fieldMessage: message = {...messageData};
  let fieldValidation = messageData?.message?.split(".")?.[0];
  fieldMessage.message = isValidObject(fieldValidation)
    ? `${fieldValidation.replace("date", fieldName)}.`
    : "";
  return fieldMessage;
};

export const isValidFirstAndLastName = (strToCheck: string) => {
  const objNamePattern =
    /^[A-Za-z\u00BF-\u0259]+[A-Za-z\u00BF-\u0259\u2018\u2019\d\s\.\,\*\'\#\(\)\"\-\/\&]*$/;

  return objNamePattern.test(strToCheck);
};

export const returnActivityNumber = (activityIdentity: string): number => {
  let activityId = activityIdentity.replace(/\D/g, "");
  return Number(activityId);
};

export const handleDateFieldBlur = (
  key: string,
  value: string | Date,
  setValue: UseFormSetValue<any>,
) => {
  try {
    if (!dayjs(value)?.isValid()) {
      setValue(key, null);
    }
  } catch (error: any) {
    loggedMessage(error, "", PageNames.ADMIN);
  }
};
export const isValidcostCenterKey = (data: string) => {
  const RegexCostCenterKey = /[\!\*\|\,"":<>[\]{}`\\';()@&$#%\/~\^\-_+=?\.]/g;
  return RegexCostCenterKey.test(data);
};

export const getStepNumber = (
  storeBreadcrumbs: BreadCrumbsProps[],
  breadCrumbId: number,
): number | undefined => {
  const tempObj = storeBreadcrumbs.find(obj => obj.id === breadCrumbId);
  return tempObj?.displayOrder;
};
export const hasOnlyNumericCommaDot = (value: string): boolean => {
  return /^[0-9.,]+$/.test(value);
};

export const isMinusOne = (val: unknown): boolean => {
  const n = Number(val);
  return Number.isFinite(n) && Math.abs(n + CPSNumbers.ONE) < Number.EPSILON;
};

export const isValidCurrencyNumber = (data: string) => {
  const Regex = /(?:^[+]?|\s)(\d{1,7}(?:,\d{3})*(?:\.\d+)?)(?=\s|$)/g;
  return Regex.test(data);
};

export const isValidCurrencyNumberNegative = (data: string) => {
  const Regex = /(?:^[+-]?|\s)(\d{1,7}(?:,\d{3})*(?:\.\d+)?)(?=\s|$)/g;
  return Regex.test(data);
};

export const toCsv = (value: string[] | string | null | undefined): string => {
  if (Array.isArray(value)) {
    return value.map(v => (v ?? "").trim()).join("|");
  }
  if (typeof value === "string") {
    // Already a CSV or single value: normalize by trimming
    return value.trim();
  }
  return "";
};

export const formatAddedWhenWithoutTimezoneShift = (
  value: string | null | undefined,
) => {
  if (!isValidObject(value)) {
    return "";
  }

  const rawValue = String(value).trim();
  const matchedDateTime = rawValue.match(
    /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/,
  );

  if (!matchedDateTime) {
    return rawValue;
  }

  const [, year, month, day, hourText, minuteText] = matchedDateTime;
  const hour24 = Number(hourText);

  if (Number.isNaN(hour24)) {
    return rawValue;
  }

  const meridiem = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const twoDigitYear = year.slice(2);
  const hour12Text = String(hour12).padStart(2, "0");

  return `${month}/${day}/${twoDigitYear} ${hour12Text}:${minuteText} ${meridiem}`;
};
